import 'bootstrap/dist/css/bootstrap.css';
import './styles/_global.scss';

import * as RoutesModule from 'Routes';

import { createContext, useContext } from 'react';

import AccountService from 'services/UserAccount/AccountService';
import AccountStore from 'stores/UserAccount/AccountStore';
import App from './App';
import Area9ProgressService from 'services/CoachSection/progress/Area9ProgressService';
import { AuthenticationService } from 'services/AuthenticationService';
import { AxiosErrorHandler } from 'services/AxiosErrorHandler';
import { AxiosInstance } from 'axios';
import { AxiosServiceConfig } from 'services/AxiosServiceConfig';
import { AxiosServiceFactory } from 'services/AxiosServiceFactory';
import BillingService from 'services/Subscription/BillingService';
import { BrowserRouter } from 'react-router-dom';
import CalculatorService from 'services/Calculator/CalculatorService';
import CalculatorStore from 'stores/Calculator/CalculatorStore';
import UserCertificateService from 'services/UserAccount/UserCertificateService';
import ClassesService from 'services/CoachSection/classes/ClassesService';
import { ConfigurationService } from 'services/ConfigurationService';
import InviteStudentsService from 'services/CoachSection/students/InviteStudentsService';
import InvoicesService from 'services/Subscription/InvoicesService';
import LanguagesService from 'services/Languages/LanguagesService';
import ManageUserDataService from 'services/UserAccount/ManageUserDataService';
import MastersEditorService from 'services/Admin/Editors/MastersEditorService';
import MastersService from 'services/Masters/MastersService';
import { NotificationsStore } from 'stores/NotificationsStore';
import OrganizationsEditorService from 'services/Admin/Editors/OrganizationsEditorService';
import OrganizationsService from 'services/CoachSection/OrganizationsService';
import ParametersService from 'services/Admin/Editors/ParametersService';
import ParametersStore from 'stores/Admin/Editors/ParametersStore';
import PartnersEditorService from 'services/Admin/Editors/PartnersEditorService';
import PartnersService from 'services/Partners/PartnersService';
import ProfileService from 'services/UserAccount/ProfileService';
import SavedCalcTableStore from 'stores/Calculator/SavedCalcTableStore';
import SendInvitationsTestService from 'services/StudentSection/SendInvitationsTestService';
import SendInvitationsTestStore from 'stores/StudentSection/SendInvitationsTestStore';
import StudentsService from 'services/CoachSection/students/StudentsService';
import TourAveragesEditorService from 'services/Admin/Editors/TourAveragesEditorService';
import TrackmanEventsEditorService from 'services/Admin/Editors/TrackmanEventsEditorService';
import TrackmanEventsService from 'services/TrackmanEventsService/TrackmanEventsService';
import TrackmanParametersService from 'services/TrackmanParameters/TrackmanParameters';
import TrackmanParametersV2PublicService from 'services/TrackmanParameters/TrackmanParametersV2';
import { TrackmanParameterCategoriesService } from 'services/Admin/Editors/TrackmanParameterCategoriesService';
import { TrackmanParametersV2Service } from 'services/Admin/Editors/TrackmanParametersV2Service';
import TranslationsService from 'services/Languages/TranslationsService';
import TranslationsStore from 'stores/Languages/TranslationsStore';
import { createRoot } from 'react-dom/client';
import registerServiceWorker from './registerServiceWorker';
import { MediaLibraryStore } from 'stores/Admin/MediaLibraryStore';
import { MediaLibraryService } from 'services/Admin/MediaLibraryService';
import LearningHubService from 'services/LearningHub/LearningHubService';
import { EditCertificationService } from 'services/Admin/Editors/EditCertificationService';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ModuleListV2Service } from 'services/Admin/Editors/ModuleListV2Service';
import { i18nInit } from 'i18nSetup';
import { CertificatesService } from 'services/Certificates/CertificatesService';
import { ModuleProgressService } from 'services/CoachSection/progress/ModuleProgressService';
import { WebshopService } from 'services/WebshopService/WebshopService';
import AdminPermissionsService from 'services/Permissions/AdminPermissionsService';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AnalyticsService } from 'services/Admin/AnalyticsService';

const rootElement = document.getElementById('root');
const root = createRoot(rootElement!);

registerServiceWorker();

type Stores = {
  // configuration stores
  errorHandler: AxiosErrorHandler;
  configurationService: ConfigurationService;
  authenticationService: AuthenticationService;
  apiServiceAxiosInstance: AxiosInstance;
  notificationStore: NotificationsStore;
  // project data stores
  translationsStore: TranslationsStore;
  sendInvitationsTestStore: SendInvitationsTestStore;
  accountStore: AccountStore;
  savedCalcTableStore: SavedCalcTableStore;
  calculatorStore: CalculatorStore;
  parametersStore: ParametersStore;
  mediaLibraryStore: MediaLibraryStore;
};

type Services = {
  moduleListV2Service: ModuleListV2Service;
  mediaLibraryService: MediaLibraryService;
  editCertificationService: EditCertificationService;
  learningHubService: LearningHubService;
  profileService: ProfileService;
  manageUserDataService: ManageUserDataService;
  adminPermissionsService: AdminPermissionsService;
  trackmanEventsService: TrackmanEventsService;
  trackmanParametersService: TrackmanParametersService;
  trackmanParametersV2PublicService: TrackmanParametersV2PublicService;
  trackmanParameterCategoriesService: TrackmanParameterCategoriesService;
  trackmanParametersV2Service: TrackmanParametersV2Service;
  webshopService: WebshopService;
  certificatesService: CertificatesService;
  moduleProgressService: ModuleProgressService;
  classesService: ClassesService;
  organizationsService: OrganizationsService;
  area9ProgressService: Area9ProgressService;
  inviteStudentsService: InviteStudentsService;
  studentsService: StudentsService;
  configurationService: ConfigurationService;
  mastersService: MastersService;
  partnersService: PartnersService;
  mastersEditorService: MastersEditorService;
  partnersEditorService: PartnersEditorService;
  languagesService: LanguagesService;
  parametersService: ParametersService;
  accountService: AccountService;
  organizationsEditorService: OrganizationsEditorService;
  analyticsService: AnalyticsService;
  trackmanEventsEditorService: TrackmanEventsEditorService;
  tourAveragesEditorService: TourAveragesEditorService;
  userCertificateService: UserCertificateService;
  billingService: BillingService;
  invoicesService: InvoicesService;
};

const StoresContext = createContext<Stores | null>(null);
const ServicesContext = createContext<Services | null>(null);

export const useStores = () => {
  const stores = useContext(StoresContext);
  if (!stores) {
    throw new Error('useStores must be used within a StoreProvider.');
  }
  return stores;
};

export const useServices = () => {
  const services = useContext(ServicesContext);
  if (!services) {
    throw new Error('useServices must be used within a ServiceProvider.');
  }
  return services;
};

async function RenderApp() {
  await i18nInit();

  const errorHandler = new AxiosErrorHandler();
  const configurationService = new ConfigurationService();
  const serviceFactory = new AxiosServiceFactory(configurationService);
  const apiServiceAxiosInstance = await serviceFactory.createInstance();
  const authenticationService = new AuthenticationService();
  const notificationStore = new NotificationsStore();

  // services
  const adminPermissionsService = new AdminPermissionsService(apiServiceAxiosInstance);
  const userCertificateService = new UserCertificateService(apiServiceAxiosInstance);
  const organizationsEditorService = new OrganizationsEditorService(apiServiceAxiosInstance);
  const classesService = new ClassesService(apiServiceAxiosInstance);
  const area9ProgressService = new Area9ProgressService(apiServiceAxiosInstance);
  const studentsService = new StudentsService(apiServiceAxiosInstance);
  const inviteStudentsService = new InviteStudentsService(apiServiceAxiosInstance);
  const invoicesService = new InvoicesService(apiServiceAxiosInstance);
  const organizationsService = new OrganizationsService(apiServiceAxiosInstance);
  const languagesService = new LanguagesService(apiServiceAxiosInstance);
  const mastersService = new MastersService(apiServiceAxiosInstance);
  const partnersService = new PartnersService(apiServiceAxiosInstance);
  const sendInvitationsTestService = new SendInvitationsTestService(apiServiceAxiosInstance);
  const billingService = new BillingService(apiServiceAxiosInstance);
  const profileService = new ProfileService(apiServiceAxiosInstance);
  const accountService = new AccountService(apiServiceAxiosInstance);
  const calculatorService = new CalculatorService(apiServiceAxiosInstance);
  const manageUserDataService = new ManageUserDataService(apiServiceAxiosInstance);
  const translationsService = new TranslationsService(apiServiceAxiosInstance);
  const parametersService = new ParametersService(apiServiceAxiosInstance);
  const trackmanParametersService = new TrackmanParametersService(apiServiceAxiosInstance);
  const trackmanParametersV2PublicService = new TrackmanParametersV2PublicService(apiServiceAxiosInstance);
  const trackmanParameterCategoriesService = new TrackmanParameterCategoriesService(apiServiceAxiosInstance);
  const trackmanParametersV2Service = new TrackmanParametersV2Service(apiServiceAxiosInstance);
  const partnersEditorService = new PartnersEditorService(apiServiceAxiosInstance);
  const mastersEditorService = new MastersEditorService(apiServiceAxiosInstance);
  const tourAveragesEditorService = new TourAveragesEditorService(apiServiceAxiosInstance);
  const trackmanEventsEditorService = new TrackmanEventsEditorService(apiServiceAxiosInstance);
  const trackmanEventsService = new TrackmanEventsService(apiServiceAxiosInstance);
  const learningHubService = new LearningHubService(apiServiceAxiosInstance);
  const mediaLibraryService = new MediaLibraryService(apiServiceAxiosInstance);
  const editCertificationService = new EditCertificationService(apiServiceAxiosInstance);
  const moduleListV2Service = new ModuleListV2Service(apiServiceAxiosInstance);
  const webshopService = new WebshopService(apiServiceAxiosInstance);
  const certificatesService = new CertificatesService(apiServiceAxiosInstance);
  const moduleProgressService = new ModuleProgressService(apiServiceAxiosInstance);
  const analyticsService = new AnalyticsService(apiServiceAxiosInstance);

  // stores
  const translationsStore = new TranslationsStore(translationsService, notificationStore);
  const sendInvitationsTestStore = new SendInvitationsTestStore(sendInvitationsTestService, notificationStore);
  const accountStore = new AccountStore(accountService, notificationStore);
  const savedCalcTableStore = new SavedCalcTableStore(notificationStore);
  const calculatorStore = new CalculatorStore(calculatorService, notificationStore);
  const parametersStore = new ParametersStore(parametersService, notificationStore);
  const mediaLibraryStore = new MediaLibraryStore(mediaLibraryService);

  const stores: Stores = {
    // configuration stores
    errorHandler,
    configurationService,
    authenticationService,
    apiServiceAxiosInstance,
    notificationStore,
    // project data stores
    translationsStore,
    sendInvitationsTestStore,
    accountStore,
    savedCalcTableStore,
    calculatorStore,
    parametersStore,
    mediaLibraryStore,
  };

  const services: Services = {
    moduleListV2Service: moduleListV2Service,
    mediaLibraryService: mediaLibraryService,
    editCertificationService: editCertificationService,
    learningHubService: learningHubService,
    profileService: profileService,
    adminPermissionsService: adminPermissionsService,
    trackmanEventsService: trackmanEventsService,
    trackmanParametersService: trackmanParametersService,
    trackmanParametersV2PublicService: trackmanParametersV2PublicService,
    trackmanParameterCategoriesService: trackmanParameterCategoriesService,
    trackmanParametersV2Service: trackmanParametersV2Service,
    webshopService: webshopService,
    certificatesService: certificatesService,
    moduleProgressService: moduleProgressService,
    classesService: classesService,
    organizationsService: organizationsService,
    area9ProgressService: area9ProgressService,
    inviteStudentsService: inviteStudentsService,
    studentsService: studentsService,
    manageUserDataService: manageUserDataService,
    configurationService: configurationService,
    mastersService: mastersService,
    partnersService: partnersService,
    mastersEditorService: mastersEditorService,
    partnersEditorService: partnersEditorService,
    languagesService: languagesService,
    parametersService: parametersService,
    accountService: accountService,
    organizationsEditorService: organizationsEditorService,
    analyticsService: analyticsService,
    trackmanEventsEditorService: trackmanEventsEditorService,
    tourAveragesEditorService: tourAveragesEditorService,
    userCertificateService: userCertificateService,
    billingService: billingService,
    invoicesService: invoicesService
  };

  translationsStore.initialize();

  // Add a request and response interceptor
  AxiosServiceConfig.setDefaultDecorators(apiServiceAxiosInstance);
  const queryClient = new QueryClient();

  root.render(
    <BrowserRouter>
      <StoresContext.Provider value={stores}>
        <ServicesContext.Provider value={services}>
          <QueryClientProvider client={queryClient}>
            <App>{RoutesModule.routes}</App>
            <ReactQueryDevtools initialIsOpen={false} />
          </QueryClientProvider>
        </ServicesContext.Provider>
      </StoresContext.Provider>
    </BrowserRouter>
  );
}

RenderApp();
